import { Component } from '@theme/component';

/**
 * Product sticky bar.
 *
 * Two jobs, both of them mirroring:
 *
 * 1. Scroll spy. Underlines the nav item whose target section the reader is
 *    currently in. Targets are read out of the rendered DOM, so adding a nav
 *    item in the editor needs no JS change.
 *
 * 2. Plan state. Copies size / term / payment from <plan-selector-component>
 *    onto its own root so the pre-rendered panel for that combination is
 *    revealed. It never resolves variant or plan ids itself and never formats
 *    money — the selector owns that, and the panels are already rendered.
 *
 * The button behaves one of two ways, set by the section's checkout mode.
 * In external mode it is an anchor whose href is promoted from the plan
 * selector to the gateway once a plan has actually been chosen. In Shopify
 * mode it delegates to the selector's own `addToCart()`, if the selector on
 * the page still has one.
 *
 * @typedef {object} StickyBarRefs
 * @property {HTMLElement} nav
 * @extends Component<StickyBarRefs>
 */
class ProductStickyBarComponent extends Component {
  requiredRefs = ['nav'];

  /** @type {IntersectionObserver | null} */
  #spy = null;
  /** @type {IntersectionObserver | null} */
  #dockWatcher = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** @type {MediaQueryList | null} */
  #mobile = null;
  /** @type {Set<Element>} */
  #visible = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#publishHeight();
    this.#resize = new ResizeObserver(() => this.#publishHeight());
    this.#resize.observe(this);
    if (this.refs.dock) this.#resize.observe(this.refs.dock);

    this.#mobile = window.matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', this.#onBreakpoint);

    document.addEventListener('plan-selector:change', this.#onPlanChange);

    // The selector renders after this section, so wait for the document before
    // taking the initial reading. Until then the Liquid defaults stand.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#init, { once: true });
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('plan-selector:change', this.#onPlanChange);
    this.#mobile?.removeEventListener('change', this.#onBreakpoint);
    this.#spy?.disconnect();
    this.#dockWatcher?.disconnect();
    this.#resize?.disconnect();
    document.documentElement.classList.remove('sticky-bar-docked');
  }

  #init = () => {
    this.#syncFromSelector();
    this.#syncCtaState();
    this.#startSpy();
    this.#onBreakpoint();
  };

  /* ------------------------------------------------------------------ */
  /* Mobile dock                                                         */
  /* ------------------------------------------------------------------ */

  #onBreakpoint = () => {
    const isMobile = this.#mobile?.matches ?? false;
    document.documentElement.classList.toggle('sticky-bar-docked', isMobile);

    this.#dockWatcher?.disconnect();
    this.#dockWatcher = null;

    if (!isMobile) {
      this.removeAttribute('data-dock-hidden');
      return;
    }

    this.#watchSelectorCta();
    this.#publishHeight();
  };

  /**
   * Withdraws the dock while the selector's own Add to Cart is on screen.
   * Falls back to always-on if that button can't be found, which is the safer
   * failure — a redundant button beats no button.
   */
  #watchSelectorCta() {
    // The selector renders one CTA per plan and hides all but the current one,
    // so watch them all. Hidden panels never intersect, which means the set
    // only ever holds the visible button.
    const targets = this.#selector?.querySelectorAll('[ref="submit"], .plan-selector__submit');
    if (!targets?.length) return;

    /** @type {Set<Element>} */
    const onScreen = new Set();

    this.#dockWatcher = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        }
        this.toggleAttribute('data-dock-hidden', onScreen.size > 0);
      },
      { threshold: 0 }
    );

    for (const target of targets) this.#dockWatcher.observe(target);
  }

  /* ------------------------------------------------------------------ */
  /* Plan state                                                          */
  /* ------------------------------------------------------------------ */

  get #selector() {
    return document.querySelector('plan-selector-component');
  }

  /** @param {Event} event */
  #onPlanChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail) return;

    // Only a real interaction reaches here — the selector doesn't fire this on
    // load — so the first one is what promotes the button from "choose" to
    // "buy". Without it the bar would offer to sell a plan the page picked.
    this.dataset.userSelected = 'true';
    this.#apply(detail.size, detail.term, detail.payment);
    this.#syncCtaState();
  };

  get #userSelected() {
    return this.dataset.userSelected === 'true';
  }

  get #external() {
    return this.dataset.checkoutMode !== 'shopify';
  }

  /**
   * Mirrors the flag onto the buttons so both states are styleable by class,
   * and promotes the href from the plan anchor to the gateway.
   *
   * Liquid renders the anchor pointing at the plan selector, so a reader
   * without JS is sent to choose rather than to checkout with whatever the
   * page defaulted to. Once a plan has genuinely been picked, the real
   * destination moves out of data-checkout-url and into href.
   */
  #syncCtaState() {
    const chosen = this.#userSelected;

    for (const cta of this.querySelectorAll('.sticky-bar__cta')) {
      cta.classList.toggle('is-ready', chosen);
      cta.classList.toggle('is-choosing', !chosen);

      if (!(cta instanceof HTMLAnchorElement)) continue;
      const { checkoutUrl, chooseHref } = cta.dataset;
      if (!checkoutUrl) continue;
      cta.href = chosen ? checkoutUrl : chooseHref || cta.href;
    }
  }

  /** Reads the selector's current state on load, including a ?variant deep link. */
  #syncFromSelector() {
    const selector = this.#selector;
    if (!(selector instanceof HTMLElement)) return;
    this.#apply(selector.dataset.size, selector.dataset.term, selector.dataset.payment);
  }

  /**
   * @param {string | undefined} size
   * @param {string | undefined} term
   * @param {string | undefined} payment
   */
  #apply(size, term, payment) {
    if (size) this.dataset.size = size;
    if (term) this.dataset.term = term;
    if (payment) this.dataset.payment = payment;
  }

  /* ------------------------------------------------------------------ */
  /* Checkout                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * External mode. Before a plan is chosen the anchor is pointing at the plan
   * selector anyway, so letting the default happen would work — but scrolling
   * smoothly reads better than a hash jump, and it keeps the behaviour
   * identical to the Shopify path.
   *
   * @param {Event} event
   */
  checkout(event) {
    if (this.#userSelected) return;
    event.preventDefault();
    this.#scrollTo(this.dataset.planAnchor || '');
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  async addToCart(event) {
    event.preventDefault();

    // Nothing chosen yet: send them to the picker rather than committing them
    // to the default. With skip-the-cart on there is no screen after this one.
    if (!this.#userSelected) {
      this.#scrollTo(this.dataset.planAnchor || '');
      return;
    }

    const selector = this.#selector;

    // No selector on the page, not upgraded yet, or a selector built for
    // external checkout that has no cart at all — send the reader to the plan
    // section rather than failing silently.
    if (!selector || typeof selector.addToCart !== 'function') {
      console.warn('[sticky-bar] Checkout mode is Shopify but the plan selector has no addToCart().');
      this.#scrollTo(this.dataset.planAnchor || '');
      return;
    }

    await selector.addToCart();
  }

  /* ------------------------------------------------------------------ */
  /* Navigation                                                          */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  goToSection(event) {
    const link = /** @type {Element} */ (event.target)?.closest?.('[data-nav-target]');
    if (!(link instanceof HTMLElement)) return;

    const id = link.dataset.navTarget;
    if (!id || !document.getElementById(id)) return;

    event.preventDefault();
    this.#scrollTo(id);

    // Reflect the destination straight away; the spy will confirm it.
    this.#markActive(id);
    history.replaceState({}, '', `#${id}`);
  }

  /** @param {string} id */
  #scrollTo(id) {
    const target = id && document.getElementById(id);
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  #startSpy() {
    const links = [...this.refs.nav.querySelectorAll('[data-nav-target]')];
    /** @type {HTMLElement[]} */
    const targets = [];

    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;
      const target = document.getElementById(link.dataset.navTarget || '');
      if (target) targets.push(target);
    }

    if (!targets.length) return;

    // Ignore the strip the bar itself occupies, and the bottom 55% of the
    // viewport, so the active item tracks what is under the bar rather than
    // whatever happens to be entering from below.
    const top = Math.round(this.getBoundingClientRect().height + this.#headerHeight());

    this.#spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visible.add(entry.target);
          else this.#visible.delete(entry.target);
        }

        // Several sections can straddle the band; the last one in document
        // order is the one the reader has scrolled into.
        const current = targets.filter((target) => this.#visible.has(target)).pop();
        if (current) this.#markActive(current.id);
      },
      { rootMargin: `-${top}px 0px -55% 0px`, threshold: 0 }
    );

    for (const target of targets) {
      this.#spy.observe(target);
    }
  }

  /** @param {string} id */
  #markActive(id) {
    for (const link of this.refs.nav.querySelectorAll('[data-nav-target]')) {
      const active = link instanceof HTMLElement && link.dataset.navTarget === id;
      if (active) {
        link.setAttribute('aria-current', 'true');
        this.#keepInView(link);
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  /** Keeps the active item visible in the horizontally scrolling mobile nav. */
  #keepInView(link) {
    const list = this.refs.nav;
    if (list.scrollWidth <= list.clientWidth) return;
    const item = link.getBoundingClientRect();
    const box = list.getBoundingClientRect();
    if (item.left >= box.left && item.right <= box.right) return;
    list.scrollTo({
      left: list.scrollLeft + (item.left - box.left) - 16,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #headerHeight() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return parseFloat(value) || 0;
  }

  #publishHeight() {
    const root = document.documentElement.style;
    const barHeight = Math.round(this.getBoundingClientRect().height);
    root.setProperty('--sticky-bar-height', `${barHeight}px`);

    // One offset for every anchor on the page, applied as scroll-padding on
    // the scroller. Per-target scroll-margin only covered the nav items, so
    // anything else — the plan anchor especially — landed under the bar.
    root.setProperty('--sticky-bar-offset', `${barHeight + this.#headerHeight() + 24}px`);

    const dock = this.refs.dock;
    const docked = this.#mobile?.matches && dock;
    root.setProperty(
      '--sticky-bar-dock-height',
      docked ? `${Math.round(dock.getBoundingClientRect().height)}px` : '0px'
    );
  }
}

if (!customElements.get('product-sticky-bar-component')) {
  customElements.define('product-sticky-bar-component', ProductStickyBarComponent);
}